import React, { useState } from 'react';
import Router from 'next/router';
import pick from 'lodash/pick';
import { makeStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import { Controller, useForm } from 'react-hook-form';
import Alert from '@material-ui/lab/Alert';
import Box from '@material-ui/core/Box';
import { Step, StepLabel, Stepper } from '@material-ui/core';
import Head from 'next/head';
import { EMAIL_REGEX } from '../src/constants';
import {
  passwordCodeCheck,
  passwordCodeRequest,
  resetPassword,
} from '../src/actions/users';

const useStyles = makeStyles((theme) => ({
  paper: {
    maxWidth: 650,
    margin: `${theme.spacing(2)}px auto`,
    padding: theme.spacing(2),
  },
  card: {
    display: 'flex',
    flexDirection: 'column',

    maxWidth: 300,
    margin: `0 auto`,
    padding: theme.spacing(2),
  },
  button: {
    marginTop: theme.spacing(2),
  },
}));

function PasswordReset() {
  const classes = useStyles();
  const [alertMessage, setAlertMessage] = useState(false);
  const [step, setStep] = useState(0);
  const [data, setData] = useState({});

  const {
    control,
    handleSubmit,
    setError,
    getValues,
    setValue,
    formState: { errors },
  } = useForm();

  const onSubmit = async (formData) => {
    setAlertMessage(null);
    let newData;
    try {
      switch (step) {
        case 0:
          if (!EMAIL_REGEX.test(formData.email)) {
            setError('email', { message: 'Die E-Mail ist ungültig' });
            return;
          }
          newData = pick(formData, 'email');
          await passwordCodeRequest(newData);
          setAlertMessage({
            mode: 'success',
            message:
              'Es wurde Ihnen ein Verifizierungscode per E-Mail zugesendet.',
          });
          break;
        case 1:
          newData = pick({ ...formData, ...data }, ['email', 'code']);
          await passwordCodeCheck(newData);
          setValue('code', '');
          break;
        case 2:
          await resetPassword(
            pick({ ...formData, ...data }, ['email', 'code', 'password']),
          );
          newData = {};
          setValue('password', '');
          setValue('repeatPassword', '');
          setAlertMessage({
            mode: 'success',
            message:
              'Ihr neues Passwort wurde erfolgreich gespeichert. Sie können sich jetzt damit anmelden.',
          });
          break;
      }
      setData(newData);
      setStep(step + 1);
    } catch (error) {
      switch (error?.data?.errCode) {
        case 'EMAIL_NOT_FOUND':
          setError('email', { message: 'E-Mail is unbekannt.' });
          break;

        case 'WRONG_CODE':
          setError('code', { message: 'Der Code ist ungültig' });
          break;

        case 'CODE_RETRIES_EXCEEDED':
          setAlertMessage(
            'Der Code wurde zu häufig falsch eingegeben. Es muss ein neuer Code generiert werden.',
          );
          setStep(0);
          setValue('code', '');
          break;

        case 'CODE_DEADLINE_EXCEEDED':
          setAlertMessage(
            'Die Gültigkeit des Codes ist abgelaufen. Es muss ein neuer Code generiert werden.',
          );
          setStep(0);
          setValue('code', '');
          break;
      }
    }
  };

  return (
    <>
      <Head>
        <title>Passwort zurücksetzen</title>
      </Head>
      <form id="pwresetForm" onSubmit={handleSubmit(onSubmit)} noValidate>
        <Paper className={classes.paper}>
          <Stepper activeStep={step} alternativeLabel>
            <Step>
              <StepLabel>Code senden</StepLabel>
            </Step>
            <Step>
              <StepLabel>Code prüfen</StepLabel>
            </Step>
            <Step>
              <StepLabel>Neues Passwort</StepLabel>
            </Step>
          </Stepper>
          {step === 0 && (
            <Box mb={1}>
              <Alert severity="info">
                Mit Absenden des Formulars erhalten Sie einen Code per E-Mail
                mit dem ein neues Passwort gesetzt werden kann.
              </Alert>
            </Box>
          )}
          {alertMessage && (
            <Box mb={1}>
              <Alert severity={alertMessage?.mode || 'error'}>
                {alertMessage?.message || alertMessage}
              </Alert>
            </Box>
          )}

          <Box className={classes.card}>
            {step < 3 ? (
              <>
                {step === 0 && (
                  <Controller
                    render={({ field }) => (
                      <TextField
                        {...field}
                        margin="dense"
                        type="email"
                        label="E-Mail"
                        fullWidth
                        helperText={errors?.email?.message}
                        error={Boolean(errors?.email)}
                        required
                      />
                    )}
                    control={control}
                    rules={{
                      required: 'E-Mail ist erforderlich',
                    }}
                    name="email"
                    defaultValue=""
                  />
                )}

                {step === 1 && (
                  <Controller
                    render={({ field }) => (
                      <TextField
                        {...field}
                        margin="dense"
                        label="Code"
                        fullWidth
                        helperText={errors?.code?.message}
                        error={Boolean(errors?.code)}
                        required
                      />
                    )}
                    control={control}
                    rules={{
                      required: 'Code ist erforderlich',
                    }}
                    name="code"
                    defaultValue=""
                  />
                )}

                {step === 2 && (
                  <>
                    <Controller
                      render={({ field }) => (
                        <TextField
                          {...field}
                          type="password"
                          label="Neues Passwort"
                          margin="dense"
                          fullWidth
                          helperText={errors?.password?.message}
                          error={Boolean(errors?.password)}
                          required
                        />
                      )}
                      control={control}
                      rules={{
                        required: 'Passwort ist erforderlich',
                        validate: (value) => {
                          const { repeatPassword } = getValues();
                          return (
                            !repeatPassword ||
                            value === repeatPassword ||
                            'Passwörter stimmen nicht überein.'
                          );
                        },
                      }}
                      name="password"
                      defaultValue=""
                    />
                    <Controller
                      render={({ field }) => (
                        <TextField
                          {...field}
                          type="password"
                          label="Neues Passwort wiederholen"
                          margin="dense"
                          fullWidth
                          helperText={errors?.repeatPassword?.message}
                          error={Boolean(errors?.repeatPassword)}
                          required
                        />
                      )}
                      control={control}
                      rules={{
                        required: 'Erforderlich',
                        validate: (value) => {
                          const { password } = getValues();
                          return (
                            !password ||
                            value === password ||
                            'Passwörter stimmen nicht überein.'
                          );
                        },
                      }}
                      name="repeatPassword"
                      defaultValue=""
                    />
                  </>
                )}

                <Button
                  color="primary"
                  className={classes.button}
                  type="submit"
                  form="pwresetForm"
                >
                  Weiter
                </Button>
              </>
            ) : (
              <Button
                color="primary"
                className={classes.button}
                onClick={() => Router.push('/login')}
              >
                Zur Anmeldung
              </Button>
            )}
          </Box>
        </Paper>
      </form>
    </>
  );
}

export default PasswordReset;
